import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isValidUKPhone, UK_PHONE_ERROR } from '../lib/phone';
import { Mail, Loader2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Site-wide newsletter capture. theme: 'dark' (footer) | 'light' (blog).
export const NewsletterSignup = ({ theme = 'light', testId = 'newsletter' }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', job_title: '', org_name: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [error, setError] = useState('');
  const dark = theme === 'dark';
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.job_title.trim() || !form.org_name.trim()) {
      setError('Please add your name, email, phone, job title and organisation');
      return;
    }
    if (!isValidUKPhone(form.phone)) { setError(UK_PHONE_ERROR); return; }
    setError('');
    setStatus('loading');
    try {
      const res = await fetch(`${API}/api/newsletter-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong');
      }
      setStatus('done');
      navigate('/thank-you-subscribing/', { state: { name: form.name.trim() } });
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setStatus('error');
    }
  };

  const labelColor = dark ? 'rgba(255,255,255,0.85)' : 'var(--dark)';
  const subColor = dark ? 'rgba(255,255,255,0.6)' : 'var(--body-text)';
  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: 18, boxSizing: 'border-box', outline: 'none',
    border: dark ? '1px solid rgba(255,255,255,0.2)' : '2px solid #e2e8f0',
    background: dark ? 'rgba(255,255,255,0.06)' : '#fff',
    color: dark ? '#fff' : 'var(--dark)',
  };

  if (status === 'done') {
    return (
      <div data-testid={`${testId}-success`} style={{ display: 'flex', alignItems: 'center', gap: 12, color: dark ? '#fff' : 'var(--dark)' }}>
        <CheckCircle size={26} style={{ color: 'var(--teal)', flexShrink: 0 }} />
        <span style={{ fontSize: 18 }}>Thanks for subscribing. We'll be in touch with practical tips for the sector.</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testId}-form`}>
      <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-heading)', fontSize: 18, letterSpacing: '0.04em', textTransform: 'uppercase', color: labelColor, marginBottom: 8 }}>
        <Mail size={16} style={{ color: 'var(--hot-pink)' }} /> Marketing tips for the sector
      </h4>
      <p style={{ fontSize: 13, color: subColor, marginBottom: 14, lineHeight: 1.6 }}>
        Join our newsletter for practical health, care and charity marketing advice. No spam, unsubscribe anytime.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
        <input data-testid={`${testId}-name`} style={inputStyle} placeholder="Your name" value={form.name} onChange={set('name')} />
        <input data-testid={`${testId}-email`} style={inputStyle} type="email" placeholder="Email address" value={form.email} onChange={set('email')} />
        <input data-testid={`${testId}-phone`} style={inputStyle} placeholder="Phone number" value={form.phone} onChange={set('phone')} />
        <input data-testid={`${testId}-job-title`} style={inputStyle} placeholder="Job title" value={form.job_title} onChange={set('job_title')} />
        <input data-testid={`${testId}-org`} style={inputStyle} placeholder="Organisation name" value={form.org_name} onChange={set('org_name')} />
      </div>
      {error && <p data-testid={`${testId}-error`} style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 10 }}>{error}</p>}
      <button data-testid={`${testId}-submit`} type="submit" disabled={status === 'loading'} className="btn-pink-filled" style={{ border: 'none', cursor: status === 'loading' ? 'default' : 'pointer' }}>
        {status === 'loading' ? <Loader2 size={15} className="spin" /> : null} SUBSCRIBE
      </button>
    </form>
  );
};

export default NewsletterSignup;
